import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Add as AddIcon, 
  Edit as EditIcon,
  Visibility as ViewIcon 
} from '@mui/icons-material';
import { fetchCustomers, createCustomer, updateCustomer } from '../store/slices/customersSlice';
import { RootState, AppDispatch } from '../store';
import { Customer } from '../types';

const Customers: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { customers, loading, totalCount } = useSelector(
    (state: RootState) => state.customers
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState({
    externalId: '',
    email: '',
    name: '',
    phone: '',
    language: 'ja',
    timezone: 'Asia/Tokyo',
  });

  useEffect(() => {
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;

    dispatch(fetchCustomers({ 
      page: page + 1, 
      pageSize: rowsPerPage, 
      filters 
    }));
  }, [dispatch, page, rowsPerPage, searchTerm]);

  const handleSearch = () => {
    setPage(0);
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;

    dispatch(fetchCustomers({ 
      page: 1, 
      pageSize: rowsPerPage, 
      filters 
    }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateClick = () => {
    setEditMode(false);
    setSelectedCustomer(null);
    setFormData({
      externalId: '',
      email: '',
      name: '',
      phone: '',
      language: 'ja',
      timezone: 'Asia/Tokyo',
    });
    setDialogOpen(true);
  };

  const handleEditClick = (customer: Customer) => {
    setEditMode(true);
    setSelectedCustomer(customer);
    setFormData({
      externalId: customer.externalId,
      email: customer.email,
      name: customer.name || '',
      phone: customer.phone || '',
      language: customer.language || 'ja',
      timezone: customer.timezone || 'Asia/Tokyo',
    });
    setDialogOpen(true);
  };

  const handleFormSubmit = async () => {
    if (editMode && selectedCustomer) {
      await dispatch(updateCustomer({ 
        id: selectedCustomer.id, 
        data: formData 
      }));
    } else {
      await dispatch(createCustomer(formData));
    }
    setDialogOpen(false);
    // Refresh the list
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;
    dispatch(fetchCustomers({ 
      page: page + 1, 
      pageSize: rowsPerPage, 
      filters 
    }));
  };

  const formatLanguage = (language: string) => {
    switch (language) {
      case 'ja': return '日本語';
      case 'en': return '英語';
      case 'ko': return '韓国語';
      case 'zh': return '中国語';
      default: return language;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        顧客管理
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="検索（メールアドレス、名前、顧客ID、外部ID）"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              検索
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              disabled={loading}
            >
              新規顧客
            </Button>
          </Grid>
        </Grid>
      </Paper>

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>顧客ID</TableCell>
                  <TableCell>外部ID</TableCell>
                  <TableCell>メールアドレス</TableCell>
                  <TableCell>名前</TableCell>
                  <TableCell>電話番号</TableCell>
                  <TableCell>言語</TableCell>
                  <TableCell>作成日時</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (!customers || customers.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (!customers || customers.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      顧客データがありません
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer: Customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell>{customer.id}</TableCell>
                      <TableCell>{customer.externalId}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.name || '-'}</TableCell>
                      <TableCell>{customer.phone || '-'}</TableCell>
                      <TableCell>{formatLanguage(customer.language)}</TableCell>
                      <TableCell>
                        {new Date(customer.createdAt).toLocaleString('ja-JP')}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(customer)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} / ${count !== -1 ? count : `${to}以上`}`
            }
          />
        </Paper>

      {/* Create/Edit Customer Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? '顧客情報編集' : '新規顧客作成'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="外部ID *"
              value={formData.externalId}
              onChange={(e) => setFormData({ ...formData, externalId: e.target.value })}
              margin="normal"
              required
              helperText="システム連携用の外部識別子"
            />
            <TextField
              fullWidth
              label="メールアドレス *"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              margin="normal"
              required
              type="email"
            />
            <TextField
              fullWidth
              label="名前 *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="電話番号"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              margin="normal"
              placeholder="+81-90-1234-5678"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>言語</InputLabel>
              <Select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                label="言語"
              >
                <MenuItem value="ja">日本語</MenuItem>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="ko">한국어</MenuItem>
                <MenuItem value="zh">中文</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>タイムゾーン</InputLabel>
              <Select
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                label="タイムゾーン"
              >
                <MenuItem value="Asia/Tokyo">Asia/Tokyo (JST)</MenuItem>
                <MenuItem value="Asia/Seoul">Asia/Seoul (KST)</MenuItem>
                <MenuItem value="Asia/Shanghai">Asia/Shanghai (CST)</MenuItem>
                <MenuItem value="UTC">UTC</MenuItem>
                <MenuItem value="America/New_York">America/New_York (EST)</MenuItem>
                <MenuItem value="Europe/London">Europe/London (GMT)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleFormSubmit} 
            variant="contained"
            disabled={!formData.email || !formData.externalId || !formData.name}
          >
            {editMode ? '更新' : '作成'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;